import { Formik, FormikHelpers } from 'formik';
import { ProReviewFormUpdateQuery, ProReviewSetInput } from 'generated/graphql';
import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';

import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Loader } from 'semantic-ui-react';
import { PhoneNumber, ToastError, ToastSuccess } from 'utils';
import { ProReviewFormFields } from './pro-review-form-fields.type';
import { proReviewFormValidationSchema } from './pro-review-form-validation-schema';
import { ProReviewFormView } from './pro-review-form-view';

type ProReviewFormUpdateContainerProps = {
  updateProReview: (proReview: ProReviewSetInput) => Promise<unknown>;
  formData?: ProReviewFormUpdateQuery['proReviewByPk'];
  loading: boolean;
};

export const ProReviewFormUpdateContainer: FunctionComponent<
  ProReviewFormUpdateContainerProps
> = ({ loading, updateProReview, formData }) => {
  const navigate = useNavigate();

  const { currentBilikPerson } = useCurrentBilikPerson();

  const initialValues: ProReviewFormFields = useMemo(() => {
    return {
      comment: formData?.comment ?? '',
      privateComment: formData?.privateComment ?? '',
      dateOfWork: formData?.dateOfWork ? new Date(formData.dateOfWork) : null,
      email: formData?.email ?? '',
      familyName: formData?.familyName ?? '',
      givenName: formData?.givenName ?? '',
      reply: formData?.reply === null ? undefined : formData?.reply,
      reviewAspect: formData?.reviewAspect ?? '',
      reviewBody: formData?.reviewBody ?? '',
      telephone: formData?.telephone ?? '',
      cityId: formData?.cityId ?? null,
      source: formData?.source === 'BILIK' ? '' : (formData?.source ?? ''),
      isExternalSource: formData?.source !== 'BILIK',
      rating: formData?.rating ?? 0,
    };
  }, [loading]);

  const hasInitialReply = useMemo(
    () => initialValues.reply !== undefined,
    [initialValues],
  );

  const onSubmit = useCallback(
    async (
      values: ProReviewFormFields,
      actions: FormikHelpers<ProReviewFormFields>,
    ) => {
      try {
        const normalize = (fields: ProReviewFormFields) => ({
          ...fields,
          cityId: fields.cityId ?? 0,
          reviewBody: fields.reviewBody.trim(),
          privateComment: fields.privateComment?.trim(),
          comment: fields.comment.trim(),
          reply: hasInitialReply ? fields.reply?.trim() : undefined, // never create a reply that didn't exist before
          email: fields.email !== 'NULL' ? fields.email.toLowerCase() : 'NULL',
          telephone:
            fields.telephone !== 'NULL'
              ? PhoneNumber.toE164(fields.telephone)
              : 'NULL',
          source:
            !fields.isExternalSource || fields.source === ''
              ? 'BILIK'
              : fields.source,
        });

        const payload = normalize(values);
        delete payload.isExternalSource;

        const normalizedInitial = normalize(initialValues);

        const hasNonReplyChanges = Object.keys(payload).some((key) => {
          if (key === 'reply') return false;
          const getComparableValue = (val: unknown) =>
            val instanceof Date ? val.getTime() : val;
          return (
            getComparableValue(normalizedInitial[key]) !==
            getComparableValue(payload[key])
          );
        });

        await updateProReview({
          ...payload,
          ...(hasNonReplyChanges && {
            lastModifiedAt: new Date().toISOString(),
          }),
        });

        ToastSuccess('Succès', 'Avis enregistré.');
        actions.setSubmitting(false);
        navigate(-1);
      } catch (e) {
        ToastError('Erreur', "Impossible d'enregistrer l'avis");
      }
    },
    [updateProReview, navigate],
  );

  const shouldDisableContentEdition = useMemo(() => {
    if (!currentBilikPerson) {
      return undefined;
    }
    return currentBilikPerson.roleName === 'user';
  }, [currentBilikPerson]);

  if (loading || shouldDisableContentEdition === undefined) {
    return (
      <Loader size="big" active inline="centered" content="Chargement..." />
    );
  }

  return (
    <Formik
      validateOnChange={true}
      validationSchema={proReviewFormValidationSchema(hasInitialReply)}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      <ProReviewFormView
        action="update"
        shouldDisableContentEdition={shouldDisableContentEdition}
      />
    </Formik>
  );
};
